import { useEffect, useMemo } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, CssBaseline, createTheme} from '@mui/material'

import { useAuthContext } from './context/auth'
import { usePathContext } from './context/path'
import { themeSettings } from './theme'
import { ROLES } from './config/roles'
import { IS_LAUNCHER, isChatOnly } from './utils/launcher'
import { useAppSelector } from './hooks/redux-hooks'
import PersistLogin from './components/PersistLogin'
import RequireAuth from './components/RequireAuth'
import RequireRoles from './components/RequireRoles'
import Recaptcha from './pages/Recaptcha'
import Signup from './pages/Signup'
import Activate from './pages/auth/Activate'
import VerifyEmail from './pages/auth/recover-password/VerifyEmail'
import Home from './pages/Home'
import User from './pages/User'
import Error from './pages/error/Error'
import NotFound from './pages/error/NotFound'
import TopLeft from './pages/TopLeft'
import Unit from './pages/Unit'
import Radar from './pages/Radar'
import ConnectLog from './pages/ConnectLog'
import Dashboard from './pages/Dashboard'
import Review from './pages/Review'
import NotificationSnackbar from './components/notification/NotificationSnackbar'
import socket, { setSocketToken } from './utils/socket'

import VPNConnectLog from './pages/VPNConnectLog'
import EditServer from './pages/EditServer'
import EditServerIP from './pages/EditServerIP'
import Chat from './pages/Chat'
import Application from './pages/Application'
import SIFCode from './pages/SIFCode'
import VPNServers from './pages/VPNServer'
import VPNServerIPs from './pages/VPNServerIPs'
import Region from './pages/Region'
import CallDialog from './components/chat/CallDialog'
import PushBridge from './components/PushBridge'
import E2EEPasscodeDialog from './components/chat/E2EEPasscodeDialog'

function App() {
  const { auth } = useAuthContext()
  const { link } = usePathContext()
  
  const authRoles = auth?.roles ?? [];
  // Guests, and every role inside the desktop launcher, only get the Chat page
  const isGuest = isChatOnly(authRoles);

  const userId = auth?._id
  const hasToken = Boolean(auth?.accessToken)

  const accessToken = auth?.accessToken

  // Always keep the latest token available for the next (re)connect.
  // This effect is declared first, so the token is set before connecting below.
  useEffect(() => {
    setSocketToken(accessToken)
  }, [accessToken])

  // Connect after login. The server identifies me from the token, so no 'online' event is needed.
  useEffect(() => {
    if (!hasToken || !userId) return

    if (!socket.connected) socket.connect()

    const heartbeat = setInterval(() => {
      if (socket.connected) socket.emit('heartbeat')
    }, 5000)

    return () => clearInterval(heartbeat)
  }, [hasToken, userId])

  // The server refuses a connection with an expired token and Socket.IO does NOT retry that by itself:
  // try again every 3 s with the newest token
  useEffect(() => {
    if (!userId) return

    let timer
    const onError = (err) => {
      if (err.message === 'token-expired' || err.message === 'unauthorized') {
        clearTimeout(timer)
        timer = setTimeout(() => {
          if (!socket.connected) socket.connect()
        }, 3000)
      }
    }

    socket.on('connect_error', onError)
    return () => {
      clearTimeout(timer)
      socket.off('connect_error', onError)
    }
  }, [userId])

  // Disconnect only on logout, or when a different user logs in
  useEffect(() => {
    if (!userId) return
    return () => socket.disconnect()
  }, [userId])

  const mode = useAppSelector((state) => state.global.mode)  // Redux state for theme mode
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode])  // MUI theme based on mode

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <CssBaseline />        
        <BrowserRouter>
          <NotificationSnackbar />
          <CallDialog />
          <E2EEPasscodeDialog />
          <PushBridge />

          <div>
            <Routes>
              <Route path="/activate/:activation_token" element={<Activate />} />
              <Route path="/recover-password" element={<VerifyEmail />} />

              <Route element={<PersistLogin />}>
                <Route path="/" element={<TopLeft />}>
                  <Route
                    path="/"
                    element={
                      isGuest && auth
                        ? <Navigate to="/chat" replace />
                        : IS_LAUNCHER
                          ? <Navigate to="/login" replace />
                          : <Home />
                    }
                  />
                  <Route
                    path="/login"
                    element={
                      !auth
                        ? <Recaptcha />
                        : <Navigate to={isGuest ? "/chat" : link} replace />
                    }
                  />
                  <Route path="/signup" element={!auth ? <Signup /> : <Navigate to="/" />} />

                  <Route element={<RequireAuth />}>    
                   {/* Chat is accessible to all authenticated roles, including Guest */}
                    <Route element={<RequireRoles Roles={[...Object.values(ROLES)]} />}>
                      <Route path="/chat" element={<Chat />} />
                    </Route>        
                    
                    {/* Regular authenticated users: Root, Admin, User */}
                    <Route element={<RequireRoles Roles={[ROLES.Root, ROLES.Admin, ROLES.User]} />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/radar" element={<Radar />} />
                      <Route path="/review" element={<Review />} />
                      <Route path="/sifcode" element={<SIFCode />} />
                      <Route path="/application" element={<Application />} />
                      <Route path="/region" element={<Region />} />
                    </Route>

                    {/* Admin and Root */}
                    <Route element={<RequireRoles Roles={[ROLES.Root, ROLES.Admin]} />}>
                      <Route path="/user" element={<User />} />      
                    </Route>       

                    {/* Root only */}     
                    <Route element={<RequireRoles Roles={[ROLES.Root]} />}>
                      <Route path="/unit" element={<Unit />} />
                      <Route path="/connectlog" element={<ConnectLog />} />                        
                      <Route path="/vpnservers" element={<VPNServers />} />
                      <Route path="/vpnservers/:serverId" element={<VPNServers />} />
                      <Route path="/vpnserverips" element={<VPNServerIPs />} />
                      <Route path="/vpnserverips/:serverId" element={<VPNServerIPs />} />
                      <Route path="/vpnconnectlog" element={<VPNConnectLog />} />
                      <Route path="/editserver/:serverId" element={<EditServer />} />
                      <Route path="/editserverip/:serverId/:serverIPId" element={<EditServerIP />} />                        
                    </Route>
                  </Route>
                </Route>
              </Route>
              
              <Route path="/error" element={<Error />} />
              <Route path="*" element={IS_LAUNCHER ? <Navigate to="/chat" replace /> : <NotFound />} />
            </Routes>
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  )
}

export default App